import React, { useRef, useState } from 'react';
import { faUpload, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UploadedDocument } from '../../../models/FlowOfFunds/flow-of-funds.model';
import { EM_DASH } from '../../../utils/flowOfFundsFormat';
import ConfirmationModal from '../../Modal/ConfirmationModal';

export interface SupportingDocumentCellProps {
    uploadedDocument: UploadedDocument | null;
    uploadLabel: string;
    isEditable: boolean;
    onUpload: (file: File) => void;
    onDelete: () => void;
    onDownload: () => void;
}

export const SupportingDocumentCell = ({
    uploadedDocument,
    uploadLabel,
    isEditable,
    onUpload,
    onDelete,
    onDownload,
}: SupportingDocumentCellProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [isConfirmingRemoval, setIsConfirmingRemoval] = useState(false);

    if (uploadedDocument) {
        return (
            <>
                <span
                    className="ff-payoffs__doc"
                    role="button"
                    tabIndex={0}
                    title="Open document"
                    onClick={onDownload}
                    onKeyDown={(e) => {
                        if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                            e.preventDefault();
                            onDownload();
                        }
                    }}>
                    <span className="ff-payoffs__doc-name">{uploadedDocument.fileName}</span>
                    {isEditable && (
                        <button
                            type="button"
                            title="Remove"
                            className="ff-payoffs__doc-remove"
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsConfirmingRemoval(true);
                            }}>
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                    )}
                </span>
                <ConfirmationModal
                    isVisible={isConfirmingRemoval}
                    setIsVisible={() => setIsConfirmingRemoval(false)}
                    header={'Are you sure?'}
                    confirmationText={`Are you sure you want to remove ${uploadedDocument.fileName}?`}
                    onConfirm={onDelete}
                    isConfirmationDanger={true}
                    cancelButtonText={'No, take me back'}
                    nextButtonText={"Yes, I'm sure"}
                />
            </>
        );
    }

    if (!isEditable) {
        return <span>{EM_DASH}</span>;
    }

    return (
        <>
            <button
                type="button"
                className="ff-payoffs__upload"
                onClick={() => inputRef.current?.click()}>
                <FontAwesomeIcon icon={faUpload} /> {uploadLabel}
            </button>
            <input
                ref={inputRef}
                type="file"
                className="ff-payoffs__file-input"
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                        onUpload(file);
                    }
                    e.target.value = '';
                }}
            />
        </>
    );
};
